import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink } from "react-router-dom";
import PageTitle from "../../components/common/PageTitle";
import PortfolioSummaryCards from "../../components/portfolio-intelligence/PortfolioSummaryCards";
import PortfolioApiTable from "../../components/portfolio-intelligence/PortfolioApiTable";
import { usePortfolioIntelligence } from "../../hooks/usePortfolioIntelligence";
import { formatRelativeScanTime } from "../../utils/portfolioIntelligenceUtils";
import { PM_DASHBOARD_PATH } from "../../utils/pmHubRoutes";

const ApiPortfolioIntelligencePage: React.FC = () => {
  const { rows, summary, loading, error } = usePortfolioIntelligence();

  const subtitle = useMemo(() => {
    if (loading) {
      return "Loading portfolio data…";
    }

    const domainLabel = summary.domainCount === 1 ? "domain" : "domains";
    return `${summary.totalApis} registered APIs across ${summary.domainCount} ${domainLabel} · Last scanned ${formatRelativeScanTime(summary.scannedAt)}`;
  }, [loading, summary]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="API portfolio intelligence"
        pageDescription={subtitle}
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          component={RouterLink}
          to={PM_DASHBOARD_PATH}
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
        >
          Back to Product Manager Dashboard
        </Button>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <PortfolioSummaryCards summary={summary} loading={loading} />

        <Alert severity="info" sx={{ mb: 3 }}>
          Duplicate scores, consumers, and call volume are estimated until portfolio
          analytics backend is available.
        </Alert>

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          API portfolio
        </Typography>

        <PortfolioApiTable rows={rows} loading={loading} />
      </Container>
    </Box>
  );
};

export default ApiPortfolioIntelligencePage;
